import apiClient from './apiClient';

/**
 * Get current user's quota status
 * @returns {Promise<Object>} Quota summary with limits, usage, and remaining
 */
export const getMyQuotas = async () => {
  const response = await apiClient.get('/quotas/my');
  return response.data.data || response.data;
};

/**
 * Admin: Get all quota definitions
 * @returns {Promise<Array>} List of quota definitions by type and role
 */
export const getQuotaDefinitions = async () => {
  const response = await apiClient.get('/admin/quotas/definitions');
  return response.data.data || response.data;
};

/**
 * Admin: Update quota definition for a role
 * @param {string} quotaType - The quota type (backtest, aiAgent, evaluation, signal)
 * @param {string} roleId - The role ID
 * @param {Object} limits - { daily: number|null, monthly: number|null }
 * @returns {Promise<Object>} Updated definition
 */
export const updateQuotaDefinition = async (quotaType, roleId, limits) => {
  const response = await apiClient.put(`/admin/quotas/definitions/${quotaType}/${roleId}`, limits);
  return response.data;
};

/**
 * Admin: Get users with quota overrides
 * @param {string} quotaType - Optional filter by quota type
 * @returns {Promise<Array>} List of users with overrides
 */
export const getUsersWithOverrides = async (quotaType = null) => {
  const params = { hasOverride: 'true' };
  if (quotaType) params.quotaType = quotaType;
  const response = await apiClient.get('/admin/quotas/users', { params });
  return response.data.data || response.data;
};

/**
 * Admin: Get quota details for a specific user
 * @param {string} accountId - The user's account ID
 * @returns {Promise<Object>} User's quota details
 */
export const getUserQuotas = async (accountId) => {
  const response = await apiClient.get(`/admin/quotas/users/${accountId}`);
  return response.data.data || response.data;
};

/**
 * Admin: Set quota override for a user
 * @param {string} accountId - The user's account ID
 * @param {string} quotaType - The quota type
 * @param {Object} override - { daily, monthly, reason, expiresAt }
 * @returns {Promise<Object>} Updated user quota
 */
export const setUserOverride = async (accountId, quotaType, override) => {
  const response = await apiClient.put(`/admin/quotas/users/${accountId}/${quotaType}`, override);
  return response.data;
};

/**
 * Admin: Remove quota override for a user
 * @param {string} accountId - The user's account ID
 * @param {string} quotaType - The quota type
 * @returns {Promise<Object>} Updated user quota
 */
export const removeUserOverride = async (accountId, quotaType) => {
  const response = await apiClient.delete(`/admin/quotas/users/${accountId}/${quotaType}`);
  return response.data;
};

/**
 * Admin: Seed default quota definitions
 * @returns {Promise<Object>} Seeding results
 */
export const seedQuotaDefinitions = async () => {
  const response = await apiClient.post('/admin/quotas/seed');
  return response.data;
};
